import { profileBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import { TransText } from '@/i18n/trans/text';
import BookingItemSkeleton from '@/routes/account/layout/BookingItemSkeleton';
import { useSelector } from '@/store/utils';
import type {
  ProfileBookingTimeType,
  ProfileBookingType,
} from '@/types/profile';
import { cn } from '@/utils/cn';
import { Icons, Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';

interface BookingNoItemsProps {
  bookingType: ProfileBookingType;
  timeType: ProfileBookingTimeType;
}

const BookingNoItems: FC<BookingNoItemsProps> = ({ bookingType, timeType }) => {
  const loading = useSelector(profileBookingSearchLoadingSelector);

  if (loading) {
    return (
      <div
        className={cn(
          'w-full',
          bookingType === 'vouchers'
            ? 'grid h-fit grid-cols-1 gap-4 laptop:grid-cols-2'
            : 'flex flex-col gap-4 laptop:max-w-[690px]'
        )}
      >
        {[1, 2, 3].map((i) => (
          <BookingItemSkeleton key={i} bookingType={bookingType} />
        ))}
      </div>
    );
  }

  const Icon =
    bookingType === 'tickets'
      ? Icons.ticket
      : bookingType === 'vouchers'
        ? Icons.email
        : Icons.travelPass;

  return (
    <div className="flex w-full flex-col items-center gap-6 py-8 laptop:max-w-[690px]">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-warning-surface">
        <Icon className="h-10 w-10 text-dark" />
      </div>
      <Typography>
        <TransText i18nKey={getTextKey(bookingType, timeType)} />
      </Typography>
    </div>
  );
};

export default BookingNoItems;

const getTextKey = (
  bookingType: ProfileBookingType,
  timeType: ProfileBookingTimeType
) => {
  if (bookingType === 'tickets') {
    return timeType === 'active' ? 'noUpcomingTickets' : 'noHistoryTickets';
  }

  if (bookingType === 'vouchers') {
    return timeType === 'active' ? 'noActiveVouchers' : 'noHistoryVouchers';
  }

  return timeType === 'active'
    ? 'noActiveTravelPasses'
    : 'noHistoryTravelPasses';
};
